import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  Badge,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material'
import {
  Add,
  Search,
  FilterList,
  Refresh,
  Download,
  Visibility,
  Edit,
  Warning,
  Delete,
  MoreVert,
  Lock,
  Archive,
  Timeline,
  Folder,
  MedicalServices,
  AutoGraph,
  People,
  DateRange,
  HourglassEmpty
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import { useAuth } from '../contexts/Authcontexts'
import { useMRD } from '../contexts/Mrdcontexts'
import { useNavigate } from 'react-router-dom'
import MRDFileForm from '../components/mrd/MRDFiles/MRDFileForm'
import MRDFileDetail from '../components/mrd/MRDFiles/MRDFileDetail'
import FileMovementHistoryDialog from '../components/mrd/FileMovement/FileMovementHistoryDialog'
import StatusChip from '../components/common/StatusChip'
import { useSnackbar } from 'notistack'
import fileStatusService from '../services/fileStatusService'
import api from '../services/api'

export const MRDFilesPage = () => {
  const { files, fetchFiles, loading, createFile, updateFile } = useMRD()
  const { user, hasPermission } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const theme = useTheme()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [wardFilter, setWardFilter] = useState('')
  const [doctorFilter, setDoctorFilter] = useState('')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalRows, setTotalRows] = useState(0)

  console.log('MRDFilesPage - files:', files)
  console.log('MRDFilesPage - files length:', files?.length)
  console.log('MRDFilesPage - totalRows:', totalRows)
  console.log('MRDFilesPage - page:', page, 'pageSize:', pageSize)
  
  const [openForm, setOpenForm] = useState(false)
  const [openDetail, setOpenDetail] = useState(false)
  const [openMovementHistory, setOpenMovementHistory] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuFile, setMenuFile] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    deficient: 0,
    completed: 0,
    closed: 0,
    archived: 0
  })

  // Calculate statistics from files
  useEffect(() => {
    if (files && files.length > 0) {
      const statsData = {
        total: files.length,
        open: files.filter(f => f.fileStatus === 'OPEN').length,
        inProgress: files.filter(f => f.fileStatus === 'IN_PROGRESS').length,
        deficient: files.filter(f => f.fileStatus === 'DEFICIENT').length,
        completed: files.filter(f => f.fileStatus === 'COMPLETED').length,
        closed: files.filter(f => f.fileStatus === 'CLOSED').length,
        archived: files.filter(f => f.fileStatus === 'ARCHIVED').length
      }
      setStats(statsData)
    }
  }, [files])

  // Load files when page or pageSize changes
  useEffect(() => {
    loadFiles()
  }, [page, pageSize])

  // Reset page and reload when filters change
  useEffect(() => {
    setAutoRefreshEnabled(false)

    if (page !== 0) {
      setPage(0)
    } else {
      loadFiles()
    }

    const timer = setTimeout(() => {
      setAutoRefreshEnabled(true)
    }, 10000)

    return () => clearTimeout(timer)
  }, [statusFilter, wardFilter, doctorFilter, fromDate, toDate])

  // Auto-refresh deficiency counts every 30 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return

    const interval = setInterval(() => {
      const hasActiveFilters = statusFilter !== 'ALL' || wardFilter || doctorFilter || fromDate || toDate
      if (!hasActiveFilters) {
        loadFiles()
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [autoRefreshEnabled, statusFilter, wardFilter, doctorFilter, fromDate, toDate])

  const loadFiles = async () => {
    const params = {
      page: page + 1,
      limit: pageSize,
      search,
      fileStatus: statusFilter !== 'ALL' ? statusFilter : undefined,
      ward: wardFilter || undefined,
      doctor: doctorFilter || undefined,
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : undefined
    }

    const response = await fetchFiles(params)
    console.log('LoadFiles response:', response)

    if (response?.pagination) {
      console.log('Setting totalRows to:', response.pagination.total)
      setTotalRows(response.pagination.total)
    } else {
      console.log('No pagination in response:', response)
      const dataLength = response?.data?.length || 0
      console.log('Using fallback totalRows:', dataLength)
      setTotalRows(dataLength)
    }
    setLastRefresh(new Date())
  }

  const handleSearch = () => {
    setPage(0)
    loadFiles()
  }

  const handleReset = () => {
    setSearch('')
    setStatusFilter('ALL')
    setWardFilter('')
    setDoctorFilter('')
    setFromDate(null)
    setToDate(null)
    setPage(0)
    setAutoRefreshEnabled(true)
    loadFiles()
  }

  const handleManualRefresh = () => {
    loadFiles()
    setLastRefresh(new Date())
  }

  const handleCloseForm = () => {
    setOpenForm(false)
    setSelectedFile(null)
  }

  const handleCreateFile = async (data) => {
    try {
      if (selectedFile) {
        await updateFile(selectedFile._id, data)
      } else {
        await createFile(data)
      }
      setOpenForm(false)
      setSelectedFile(null)
      loadFiles()
    } catch (error) {
      // Error handled in service
    }
  }

  const handleViewDetail = (file) => {
    console.log('View detail clicked for file:', file)
    setSelectedFile(file)
    setOpenDetail(true)
  }

  const handleViewMovementHistory = (file) => {
    console.log('View movement history clicked for file:', file._id)
    setSelectedFile(file)
    setOpenMovementHistory(true)
  }

  const handleMenuOpen = (event, file) => {
    console.log('Menu opened for file:', file._id)
    setAnchorEl(event.currentTarget)
    setMenuFile(file)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuFile(null)
  }

  const StatCard = ({ title, value, color, icon: Icon }) => (
    <Card sx={{ 
      height: '100%',
      borderRadius: 3,
      borderLeft: `4px solid ${color}`,
      bgcolor: alpha(color, 0.1)
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Icon sx={{ color, fontSize: 28, mr: 1 }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" fontWeight={700} color="text.primary">
          {value}
        </Typography>
      </CardContent>
    </Card>
  )

  const columns = [
    {
      field: 'ipdNumber',
      headerName: 'IPD No',
      width: 120,
      renderCell: (params) => (
        <Typography variant="h6" fontWeight={600} color="primary">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'patientName',
      headerName: 'Patient',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            UHID: {params.row.uhid}
          </Typography>
        </Box>
      )
    },
    {
      field: 'ward',
      headerName: 'Ward',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body1" fontWeight={500}>
          {params?.row?.ward?.name || '-'}
        </Typography>
      )
    },
    {
      field: 'doctor',
      headerName: 'Doctor',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body1" fontWeight={500}>
          {params?.row?.doctor?.name ? `Dr. ${params.row.doctor.name}` : '-'}
        </Typography>
      )
    },
    {
      field: 'dischargeDate',
      headerName: 'Discharge Date',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body1">
          {params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '-'}
        </Typography>
      )
    },
    {
      field: 'fileStatus',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <StatusChip status={params.value} />
    },
    {
      field: 'deficiencyCount',
      headerName: 'Deficiencies',
      width: 180,
      renderCell: (params) => {
        const total = params.value || 0;
        const pending = params.row.pendingDeficiencyCount || 0;
        const completed = params.row.completedDeficiencyCount || 0;
        const waived = params.row.waivedDeficiencyCount || 0;
        const overdue = params.row.overdueDeficiencyCount || 0;

        if (total === 0) {
          return (
            <Chip
              label="No Issues"
              size="medium"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`${pending} Pending`}
                size="small"
                color={pending > 0 ? 'error' : 'default'}
                variant="outlined"
                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
              />
              {overdue > 0 && (
                <Chip
                  label={`${overdue} Overdue`}
                  size="small"
                  color="error"
                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {completed} Done • {waived} Waived • {total} Total
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const buttonVisibility = fileStatusService.getButtonVisibility(params.row.fileStatus, user?.role);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                size="medium"
                onClick={() => handleViewDetail(params.row)}
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                }}
              >
                <Visibility fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Movement History">
              <IconButton
                size="medium"
                onClick={() => handleViewMovementHistory(params.row)}
                sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                }}
              >
                <Timeline fontSize="small" color="info" />
              </IconButton>
            </Tooltip>

            {buttonVisibility.edit && (
              <Tooltip title="Edit File">
                <IconButton
                  size="medium"
                  onClick={() => {
                    console.log('Edit button clicked for file:', params.row._id)
                    setSelectedFile(params.row)
                    setOpenForm(true)
                  }}
                  sx={{ 
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) }
                  }}
                >
                  <Edit fontSize="small" color="secondary" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="More Options">
              <IconButton
                size="medium"
                onClick={(e) => handleMenuOpen(e, params.row)}
                sx={{ 
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
                }}
              >
                <MoreVert fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ]

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          pb: 3,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <MedicalServices sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h3" fontWeight={700} color="primary">
                MRD Files Management
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary" sx={{ ml: 6 }}>
              Manage patient medical records and track file status
              <Chip 
                label={`Last updated: ${format(lastRefresh, 'HH:mm:ss')}`}
                size="small"
                sx={{ ml: 2, fontSize: '0.75rem' }}
              />
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {hasPermission('MRD_STAFF') && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setSelectedFile(null)
                  setOpenForm(true)
                }}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 3
                }}
              >
                Create New File
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleManualRefresh}
              sx={{ 
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 3
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="Total Files" 
              value={stats.total} 
              color={theme.palette.primary.main}
              icon={Folder}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="Open" 
              value={stats.open} 
              color={theme.palette.info.main}
              icon={HourglassEmpty}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="In Progress" 
              value={stats.inProgress} 
              color={theme.palette.warning.main}
              icon={AutoGraph}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="Deficient" 
              value={stats.deficient} 
              color={theme.palette.error.main}
              icon={Warning}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="Completed" 
              value={stats.completed} 
              color={theme.palette.success.main}
              icon={MedicalServices}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <StatCard 
              title="Closed/Archived" 
              value={stats.closed + stats.archived} 
              color={theme.palette.grey[600]}
              icon={Lock}
            />
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Paper sx={{ 
          p: 4, 
          mb: 4,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FilterList sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={600} color="primary">
              Search & Filter
            </Typography>
          </Box>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search IPD/Patient Name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                size="medium"
                InputProps={{
                  sx: { fontSize: '1rem', borderRadius: 2 },
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2, fontSize: '1rem' }}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="DEFICIENT">Deficient</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                  <MenuItem value="ARCHIVED">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      size: "medium",
                      sx: { borderRadius: 2, '& input': { fontSize: '1rem' } }
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      size: "medium",
                      sx: { borderRadius: 2, '& input': { fontSize: '1rem' } }
                    } 
                  }}
                />
              </Grid>
            </LocalizationProvider>

            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<Search />}
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Auto-refresh status */}
          <Alert 
            severity="info" 
            sx={{ 
              mt: 3,
              borderRadius: 2,
              fontSize: '0.9rem',
              '& .MuiAlert-icon': { fontSize: '1.2rem' }
            }}
          >
            {autoRefreshEnabled && statusFilter === 'ALL' && !wardFilter && !doctorFilter && !fromDate && !toDate
              ? '📡 Auto-refresh is enabled - data updates every 30 seconds'
              : '⏸️ Auto-refresh paused - active filters detected'
            }
          </Alert>
        </Paper>
      </Box>

      {/* Data Grid */}
      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        {loading && <LinearProgress />}
        
        <Box sx={{ 
          p: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }}>
          <Typography variant="h6" fontWeight={600} color="primary">
            MRD Files List ({totalRows} files)
          </Typography>
        </Box>
        
        <DataGrid
          rows={files || []}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={{
            page: page,
            pageSize: pageSize
          }}
          onPaginationModelChange={(model) => {
            console.log('Pagination model changed:', model)
            setPage(model.page)
            setPageSize(model.pageSize)
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          disableSelectionOnClick
          getRowId={(row) => row._id}
          loading={loading}
          autoHeight
          density="comfortable"
          sx={{
            border: 'none',
            fontSize: '0.95rem',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              fontSize: '1rem',
              fontWeight: 600,
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.95rem'
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            },
            '& .MuiDataGrid-cell': {
              padding: '12px 16px',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              fontSize: '0.95rem'
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              fontSize: '0.9rem'
            }
          }}
        />
      </Paper>

      {/* Create/Edit Form Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          py: 3,
          borderBottom: `2px solid ${alpha('#fff', 0.1)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MedicalServices sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              {selectedFile ? 'Edit MRD File' : 'Create New MRD File'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <MRDFileForm
            onSubmit={handleCreateFile}
            onCancel={handleCloseForm}
            initialData={selectedFile}
          />
        </DialogContent>
      </Dialog>

      {/* File Detail Dialog */}
      <Dialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedFile && (
            <MRDFileDetail
              file={selectedFile}
              onClose={() => setOpenDetail(false)}
              onStatusChange={loadFiles}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Button 
            onClick={() => setOpenDetail(false)}
            variant="outlined"
            sx={{ 
              px: 4,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* More Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => {
          handleViewDetail(menuFile)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Visibility fontSize="medium" color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="View Details" 
            primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem onClick={() => {
          handleViewMovementHistory(menuFile)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Timeline fontSize="medium" color="info" />
          </ListItemIcon>
          <ListItemText 
            primary="Movement History" 
            primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
          />
        </MenuItem>

        {menuFile && fileStatusService.getButtonVisibility(menuFile.fileStatus, user?.role).deficiencies && (
          <MenuItem onClick={() => {
            navigate(`/hospital/mrd/deficiency/${menuFile?._id}`)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <Warning fontSize="medium" color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary="Check Deficiencies" 
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </MenuItem>
        )}

        <Divider />

        {menuFile && fileStatusService.getButtonVisibility(menuFile.fileStatus, user?.role).medicalCoding && (
          <MenuItem onClick={() => {
            navigate(`/hospital/mrd/coding/${menuFile?._id}/edit`)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <Edit fontSize="medium" color="secondary" />
            </ListItemIcon>
            <ListItemText 
              primary="Medical Coding" 
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          navigate(`/hospital/mrd/documents/${menuFile?._id}`)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Folder fontSize="medium" color="action" />
          </ListItemIcon>
          <ListItemText 
            primary="View Documents" 
            primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
          />
        </MenuItem>

        <Divider />

        {menuFile && fileStatusService.getButtonVisibility(menuFile.fileStatus, user?.role).close && (
          <MenuItem onClick={() => {
            navigate(`/hospital/mrd/closure/${menuFile?._id}`)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <Lock fontSize="medium" color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="Close File" 
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </MenuItem>
        )}

        {menuFile && fileStatusService.getButtonVisibility(menuFile.fileStatus, user?.role).archive && (
          <MenuItem onClick={() => {
            navigate(`/hospital/mrd/archive?fileId=${menuFile?._id}`)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <Archive fontSize="medium" color="action" />
            </ListItemIcon>
            <ListItemText 
              primary="Archive File" 
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* File Movement History Dialog */}
      <FileMovementHistoryDialog
        open={openMovementHistory}
        onClose={() => setOpenMovementHistory(false)}
        file={selectedFile}
      />
    </Container>
    </Box>
  )
}